import type { ProductQualificationAnswer, ProductQualificationQuestion, ProductQualificationSubmission } from '@bloom/shared';

export interface CheckoutQualificationProduct {
  _id: string;
  displayName: string;
  qualification: { questions: ProductQualificationQuestion[]; version: string; requiresMeasurements: boolean };
}
export type CheckoutQualificationAnswers = Record<string, ProductQualificationAnswer>;

/** Only explicitly shared IDs with identical wording and rules can share an answer.
 * A product version may change without invalidating its unchanged questions.
 */
export function qualificationQuestionKey(q: ProductQualificationQuestion): string {
  return JSON.stringify([q.id, q.prompt, q.required, q.sex ?? null, q.detailsWhen ?? null, q.detailsPrompt ?? null, q.reviewWhen ?? null]);
}

export function combinedQualificationQuestions(products: CheckoutQualificationProduct[], sex: string) {
  const combined = new Map<string, { key: string; question: ProductQualificationQuestion; productNames: string[] }>();
  for (const product of products) {
    for (const question of product.qualification.questions) {
      if (question.sex && question.sex !== sex) continue;
      const key = qualificationQuestionKey(question);
      const existing = combined.get(key);
      if (existing) {
        if (!existing.productNames.includes(product.displayName)) existing.productNames.push(product.displayName);
      } else combined.set(key, { key, question, productNames: [product.displayName] });
    }
  }
  return [...combined.values()];
}

/** Expand shared answers back into each product's own versioned contract. */
export function qualificationSubmissions(products: CheckoutQualificationProduct[], answers: CheckoutQualificationAnswers, sex: '' | 'male' | 'female'): ProductQualificationSubmission[] {
  return products.filter(p => p.qualification.questions.length).map(product => ({
    productId: product._id, version: product.qualification.version, biologicalSex: sex || undefined,
    answers: Object.fromEntries(product.qualification.questions.filter(q => !q.sex || q.sex === sex).flatMap(q => {
      const answer = answers[qualificationQuestionKey(q)];
      return answer ? [[q.id, { value: answer.value, ...(q.detailsWhen === answer.value ? { details: answer.details } : {}) }]] : [];
    })),
  }));
}
